import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TableFooter,
} from "@material-ui/core";
import { map } from "ramda";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { makeStyles } from "@material-ui/core/styles";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import Typography from "@material-ui/core/Typography";
import TablePagination from "@material-ui/core/TablePagination";
import { useMediaErrors } from "./processingDiscoveryServices";
import { DateTime } from "luxon";
import Tooltip from "@material-ui/core/Tooltip";
import OpenIcon from "@material-ui/icons/Launch";
import IconButton from "@material-ui/core/IconButton";
import ContentMetadataParseErrorDialog from "./ContentMetadataParseErrorDialog";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },

  cell: {
    minWidth: 200,
    maxWidth: 600,
  },

  countCell: {
    minWidth: 50,
    maxWidth: 60,
  },
}));

const ContentMetadataErrorsCard = ({ jobId, mediaId }) => {
  const classes = useStyles();

  const [metadataErrors, setMetadataErrors] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const errorsResponse = useMediaErrors(jobId, mediaId, page);

  useEffect(() => {
    if (errorsResponse.isSuccess && notBlank(errorsResponse.data)) {
      setMetadataErrors(errorsResponse.data.content);
      setTotal(errorsResponse.data.totalElements);
    } else {
      setMetadataErrors([]);
      setTotal(0);
    }
  }, [errorsResponse.isSuccess, errorsResponse.data]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentError, setCurrentError] = useState(null);
  const handleViewClick = useCallback(
    metadataError => {
      setCurrentError(metadataError);
      setDialogOpen(true);
    },
    [setCurrentError, setDialogOpen],
  );

  const handleDialogCancel = useCallback(() => {
    setDialogOpen(false);
    setCurrentError(null);
  }, [setCurrentError, setDialogOpen]);

  const handlePageChange = useCallback(page => setPage(page), []);

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Content Metadata Errors"
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {errorsResponse.isError && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            <Typography color="error">
              Error loading Content Metadata Errors.
            </Typography>
          </Box>
        )}
        {errorsResponse.isLoading && <PanelLoadingIndicator />}
        {errorsResponse.isSuccess && isBlank(metadataErrors) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Errors with the Content Metadata Event.
          </Box>
        )}
        {errorsResponse.isSuccess && notBlank(metadataErrors) && (
          <TableContainer>
            <Table stickyHeader aria-label="Content Metadata Errors">
              <TableHead>
                <TableRow>
                  <TableCell
                    key="eventTimestamp"
                    align="left"
                    className={classes.contentCell}
                  >
                    Timestamp
                  </TableCell>

                  <TableCell
                    key="eventType"
                    align="left"
                    className={classes.cell}
                  >
                    Type
                  </TableCell>

                  <TableCell
                    key="taskName"
                    align="left"
                    className={classes.cell}
                  >
                    Task Name
                  </TableCell>

                  <TableCell
                    key="taskRetry"
                    align="center"
                    className={classes.countCell}
                  >
                    Task Retry Count
                  </TableCell>

                  <TableCell
                    key="actions"
                    align="center"
                    style={{ width: 100 }}
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {map(
                  metadataError => (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={
                        metadataError.jobId +
                        DateTime.fromISO(
                          metadataError.eventTimestamp,
                        ).toMillis()
                      }
                    >
                      <TableCell
                        key={
                          "eventTimestamp:" +
                          metadataError.jobId +
                          DateTime.fromISO(
                            metadataError.eventTimestamp,
                          ).toMillis()
                        }
                        align="left"
                        className={classes.contentCell}
                      >
                        {DateTime.fromISO(metadataError.eventTimestamp)
                          .toUTC()
                          .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}
                      </TableCell>

                      <TableCell
                        key={
                          "eventType:" +
                          metadataError.jobId +
                          DateTime.fromISO(
                            metadataError.eventTimestamp,
                          ).toMillis()
                        }
                        align="left"
                      >
                        {metadataError.eventType}
                      </TableCell>

                      <TableCell
                        key={
                          "taskName:" +
                          metadataError.jobId +
                          DateTime.fromISO(
                            metadataError.eventTimestamp,
                          ).toMillis()
                        }
                        align="left"
                      >
                        {metadataError.taskName}
                      </TableCell>

                      <TableCell
                        key={
                          "taskRetry:" +
                          metadataError.jobId +
                          DateTime.fromISO(
                            metadataError.eventTimestamp,
                          ).toMillis()
                        }
                        align="center"
                      >
                        {metadataError.taskRetry}
                      </TableCell>

                      <TableCell
                        key={
                          "actions:" +
                          metadataError.jobId +
                          DateTime.fromISO(
                            metadataError.eventTimestamp,
                          ).toMillis()
                        }
                        align="center"
                        style={{ width: 100 }}
                      >
                        <Box display="flex" justifyContent="space-between">
                          <Tooltip title="View">
                            <IconButton
                              onClick={() => handleViewClick(metadataError)}
                            >
                              <OpenIcon fontSize="large" color="primary" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ),
                  metadataErrors,
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    classes={{
                      root: classes.pagination,
                    }}
                    rowsPerPageOptions={[10]}
                    colSpan={5}
                    count={total}
                    rowsPerPage={10}
                    page={page}
                    onChangePage={(event, page) => handlePageChange(page)}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {dialogOpen && (
        <ContentMetadataParseErrorDialog
          open={dialogOpen}
          contentMetadataParseError={currentError}
          onCancel={handleDialogCancel}
        />
      )}
    </Card>
  );
};

export default ContentMetadataErrorsCard;
